import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  useSurveyAssignment,
  useSurveyResponse,
  useUpsertSurveyAnswer,
  useCompleteSurveyResponse,
} from "@/hooks/useSurveyCompletion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, Star } from "lucide-react";
import { format } from "date-fns";
import eabLogo from "@/assets/eab-logo-horizontal.png";
import type { PlatformSurveyQuestion } from "@/hooks/usePlatformSurveys";

const ORANGE = "#C94F13";
const GREEN = "#8FCA3F";
const FONT_STACK = "'Public Sans', Arial, sans-serif";

type AnswerValue = {
  text?: string;
  number?: number;
  choice?: string;
};

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="min-h-screen bg-background py-8 px-4"
      style={{ fontFamily: FONT_STACK }}
    >
      <div className="max-w-[640px] mx-auto">
        <div className="flex justify-center mb-6">
          <img src={eabLogo} alt="EAB Connect" className="h-12 w-auto" />
        </div>
        {children}
      </div>
    </div>
  );
}

function FullMessage({
  title,
  body,
  actionLabel = "Go to Dashboard",
  onAction,
}: {
  title: string;
  body: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  const navigate = useNavigate();
  return (
    <PageShell>
      <Card>
        <CardContent className="p-8 text-center space-y-4">
          <h1 className="text-2xl font-bold">{title}</h1>
          <p className="text-muted-foreground">{body}</p>
          <Button onClick={onAction || (() => navigate("/dashboard"))}>{actionLabel}</Button>
        </CardContent>
      </Card>
    </PageShell>
  );
}

function CompletionState({ onTime }: { onTime: boolean }) {
  const navigate = useNavigate();
  return (
    <PageShell>
      <Card>
        <CardContent className="p-8 text-center space-y-4">
          <div className="flex justify-center">
            <CheckCircle2 className="h-16 w-16" style={{ color: GREEN }} />
          </div>
          <h1 className="text-3xl font-bold">Thank you!</h1>
          <p className="text-muted-foreground">
            Your response has been recorded. EAB appreciates your feedback.
          </p>
          {onTime && (
            <p className="text-sm" style={{ color: GREEN }}>
              You completed this survey on time.
            </p>
          )}
          <Button onClick={() => navigate("/dashboard")} className="mt-4">
            Return to Dashboard
          </Button>
        </CardContent>
      </Card>
    </PageShell>
  );
}

function QuestionInput({
  q,
  value,
  onChange,
}: {
  q: PlatformSurveyQuestion;
  value: AnswerValue;
  onChange: (v: AnswerValue) => void;
}) {
  if (q.question_type === "multiple_choice") {
    const opts: string[] = Array.isArray(q.options)
      ? q.options
      : Array.isArray((q.options as any)?.options)
        ? (q.options as any).options
        : [];
    return (
      <RadioGroup
        value={value.choice ?? ""}
        onValueChange={(v) => onChange({ choice: v })}
        className="space-y-2"
      >
        {opts.map((opt, i) => (
          <div
            key={i}
            className="flex items-center space-x-2 border rounded-md p-3 hover:bg-accent/30 cursor-pointer"
            onClick={() => onChange({ choice: opt })}
          >
            <RadioGroupItem value={opt} id={`opt-${q.id}-${i}`} />
            <Label htmlFor={`opt-${q.id}-${i}`} className="flex-1 cursor-pointer">
              {opt}
            </Label>
          </div>
        ))}
      </RadioGroup>
    );
  }

  if (q.question_type === "rating") {
    const labels: Record<number, string> = { 1: "Poor", 3: "Neutral", 5: "Excellent" };
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          {[1, 2, 3, 4, 5].map((n) => {
            const selected = value.number === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => onChange({ number: n })}
                className="flex-1 flex flex-col items-center gap-1 p-3 rounded-md border transition-colors"
                style={{
                  backgroundColor: selected ? ORANGE : "transparent",
                  color: selected ? "white" : undefined,
                  borderColor: selected ? ORANGE : undefined,
                }}
                aria-label={`Rating ${n}`}
              >
                <Star className="h-6 w-6" fill={selected ? "white" : "none"} />
                <span className="text-sm font-medium">{n}</span>
              </button>
            );
          })}
        </div>
        <div className="flex justify-between text-xs text-muted-foreground px-1">
          <span>{labels[1]}</span>
          <span>{labels[3]}</span>
          <span>{labels[5]}</span>
        </div>
      </div>
    );
  }

  if (q.question_type === "text") {
    return (
      <Textarea
        rows={4}
        value={value.text ?? ""}
        onChange={(e) => onChange({ text: e.target.value })}
        placeholder="Type your response here…"
      />
    );
  }

  if (q.question_type === "number") {
    return (
      <Input
        type="number"
        value={value.number ?? ""}
        onChange={(e) =>
          onChange({ number: e.target.value === "" ? undefined : Number(e.target.value) })
        }
        placeholder="Enter a number"
      />
    );
  }

  if (q.question_type === "currency") {
    return (
      <div className="flex items-center">
        <span className="px-3 py-2 border border-r-0 rounded-l-md bg-muted text-muted-foreground">
          $
        </span>
        <Input
          type="number"
          step="0.01"
          className="rounded-l-none"
          value={value.number ?? ""}
          onChange={(e) =>
            onChange({ number: e.target.value === "" ? undefined : Number(e.target.value) })
          }
          placeholder="0.00"
        />
      </div>
    );
  }

  return null;
}

function answerToValue(a: any | undefined): AnswerValue {
  if (!a) return {};
  return {
    text: a.answer_text ?? undefined,
    number: a.answer_number ?? undefined,
    choice: a.answer_choice?.selected ?? undefined,
  };
}

function valueToPayload(q: PlatformSurveyQuestion, v: AnswerValue) {
  switch (q.question_type) {
    case "multiple_choice":
      return { answer_choice: v.choice ? { selected: v.choice } : null };
    case "rating":
    case "number":
    case "currency":
      return { answer_number: typeof v.number === "number" ? v.number : null };
    case "text":
      return { answer_text: v.text?.trim() ? v.text : null };
    default:
      return {};
  }
}

function isAnswered(q: PlatformSurveyQuestion, v: AnswerValue) {
  switch (q.question_type) {
    case "multiple_choice":
      return !!v.choice;
    case "rating":
    case "number":
    case "currency":
      return typeof v.number === "number" && !Number.isNaN(v.number);
    case "text":
      return !!v.text?.trim();
    default:
      return false;
  }
}

export default function SurveyPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  const { assignment, survey, questions, isLoading: aLoading, error: aError } =
    useSurveyAssignment(assignmentId);
  const { response, answers, isLoading: rLoading, refresh } = useSurveyResponse(
    assignmentId,
    assignment?.survey_id,
    assignment?.wave_label,
  );

  const upsertAnswer = useUpsertSurveyAnswer();
  const completeResponse = useCompleteSurveyResponse();

  const [idx, setIdx] = useState(0);
  const [values, setValues] = useState<Record<string, AnswerValue>>({});
  const [validation, setValidation] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!hydrated && answers && answers.length > 0) {
      const seed: Record<string, AnswerValue> = {};
      for (const a of answers) seed[(a as any).question_id] = answerToValue(a);
      setValues(seed);
      setHydrated(true);
    } else if (!hydrated && response) {
      setHydrated(true);
    }
  }, [answers, response, hydrated]);

  const onTime = useMemo(() => {
    if (!assignment?.due_at) return false;
    return new Date(assignment.due_at).getTime() >= Date.now();
  }, [assignment?.due_at]);

  if (authLoading || aLoading || rLoading) {
    return (
      <PageShell>
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PageShell>
    );
  }

  if (aError || !assignment) {
    return (
      <FullMessage
        title="Survey unavailable"
        body="This survey link is not valid for your account."
      />
    );
  }

  if (!survey) {
    return <FullMessage title="Not found" body="This survey is no longer available." />;
  }

  if (response?.completed_at) {
    return <CompletionState onTime={onTime} />;
  }

  if (!questions || questions.length === 0) {
    return <FullMessage title="Not ready" body="This survey has no questions yet." />;
  }

  const total = questions.length;
  const safeIdx = Math.min(idx, total - 1);
  const q = questions[safeIdx];
  const currentValue = values[q.id] ?? {};
  const isLast = safeIdx === total - 1;
  const progress = ((safeIdx + 1) / total) * 100;

  const handleSaveAndAdvance = async (advance: 1 | "submit") => {
    setValidation(null);
    setSaveError(null);
    if (q.required && !isAnswered(q, currentValue)) {
      setValidation("This question requires an answer.");
      return;
    }
    if (!response) {
      setSaveError("Your answer could not be saved. Please try again.");
      return;
    }

    if (isAnswered(q, currentValue)) {
      try {
        await upsertAnswer.mutateAsync({
          response_id: response.id,
          question_id: q.id,
          ...valueToPayload(q, currentValue),
        });
      } catch (error) {
        console.error("Full Supabase error:", JSON.stringify(error, null, 2));
        setSaveError("Your answer could not be saved. Please try again.");
        return;
      }
    }

    if (advance === "submit") {
      try {
        await completeResponse.mutateAsync(response.id);
        refresh();
      } catch (error) {
        console.error("Full Supabase error:", JSON.stringify(error, null, 2));
        setSaveError("Your answer could not be saved. Please try again.");
      }
    } else {
      setIdx((i) => Math.min(i + 1, total - 1));
    }
  };

  const handleBack = () => {
    setValidation(null);
    setSaveError(null);
    setIdx((i) => Math.max(0, i - 1));
  };

  return (
    <PageShell>
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <h1 className="text-2xl sm:text-3xl font-bold">{survey.title}</h1>
          {survey.description && (
            <p className="text-muted-foreground">{survey.description}</p>
          )}
          {assignment.due_at && (
            <p className="text-sm text-muted-foreground">
              Due {format(new Date(assignment.due_at), "PPP")}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>
              Question {safeIdx + 1} of {total}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress
            value={progress}
            className="h-2 [&>div]:bg-[color:var(--eab-orange)]"
            style={{ ["--eab-orange" as any]: ORANGE }}
          />
        </div>

        <Card className="shadow-sm">
          <CardContent className="p-6 space-y-5">
            <div>
              <h2 className="text-lg font-semibold">
                {q.question_text}
                {q.required && <span className="text-destructive ml-1">*</span>}
              </h2>
            </div>

            <QuestionInput
              q={q}
              value={currentValue}
              onChange={(v) => {
                setValues((prev) => ({ ...prev, [q.id]: v }));
                setValidation(null);
              }}
            />

            {validation && (
              <p className="text-sm text-destructive">{validation}</p>
            )}
            {saveError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {saveError}
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={safeIdx === 0 || upsertAnswer.isPending}
              >
                Back
              </Button>
              {isLast ? (
                <Button
                  onClick={() => handleSaveAndAdvance("submit")}
                  disabled={upsertAnswer.isPending || completeResponse.isPending}
                  style={{ backgroundColor: ORANGE }}
                >
                  {(upsertAnswer.isPending || completeResponse.isPending) && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  Submit
                </Button>
              ) : (
                <Button
                  onClick={() => handleSaveAndAdvance(1)}
                  disabled={upsertAnswer.isPending}
                  style={{ backgroundColor: ORANGE }}
                >
                  {upsertAnswer.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Next
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
}
